import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Pagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { breakLabel, formatDuration, formatTime, isoDate } from "../../../utils/attendance";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import { AGENT_INK, AGENT_LINE, AGENT_MUTED } from "../../../agentComponents/agentTheme";
import { useAttendanceEvents } from "../../../services/attendanceSocket";
import { formatCoords, mapsUrl } from "../../../utils/geolocation";

// The agent's own past shifts. Read-only on purpose: an agent correcting their
// own hours defeats the point of the record, so fixes go through an admin.
//
// Laid out as rows rather than a dense grid -- each day gets a progress bar
// against the target, which is the number an agent actually looks for.

const TARGET_HOURS = 8;
const PAGE_SIZE = 12;

const dayParts = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return { day: iso, weekday: "" };
  return {
    day: d.toLocaleDateString([], { day: "2-digit", month: "short" }),
    weekday: d.toLocaleDateString([], { weekday: "short" }),
  };
};

export default function AttendanceHistory({ refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [range, setRange] = useState({ from: isoDate(29), to: isoDate(0) });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(apiEndPointsConfig.attendanceMyHistory, {
        params: { from: range.from, to: range.to, page, limit: PAGE_SIZE },
      });
      setRows(data?.data || []);
      setPages(data?.pagination?.pages || 1);
      setTotal(data?.pagination?.total || 0);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, page]);

  // Two things make this list stale, and they need different signals:
  // `refreshKey` covers an action taken on this page (clocking out adds a row),
  // and the socket covers one taken elsewhere -- a second tab, or an admin
  // closing the shift for you.
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const reload = useCallback(() => {
    if (page === 1) load();
    else setPage(1); // a finished shift lands at the top, so go there
  }, [load, page]);

  useAttendanceEvents("attendance.self", reload, reload);

  const dateField = (label, key) => (
    <TextField
      type="date"
      size="small"
      label={label}
      InputLabelProps={{ shrink: true }}
      value={range[key]}
      onChange={(e) => {
        setPage(1);
        setRange((r) => ({ ...r, [key]: e.target.value }));
      }}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, width: 160 }}
    />
  );

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: `1px solid ${AGENT_LINE}`, p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: AGENT_INK }}>
            Attendance Log
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: AGENT_MUTED }}>
            Your recorded shifts. Contact your admin for corrections.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {dateField("From", "from")}
          {dateField("To", "to")}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={26} />
        </Box>
      ) : !rows.length ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13.5, color: AGENT_MUTED }}>
            No shifts recorded in this period.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Column headings, desktop only -- the cards below carry their own
              labels at narrow widths. */}
          <Box
            sx={{
              display: { xs: "none", md: "grid" },
              gridTemplateColumns: "118px 1fr 120px 120px 150px",
              gap: 2,
              px: 2,
              pb: 1,
              borderBottom: `1px solid ${AGENT_LINE}`,
            }}
          >
            {["Date", "Shift", "Effective", "Break", "Status"].map((h) => (
              <Typography
                key={h}
                sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#94A3B8" }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {rows.map((r) => {
              const { day, weekday } = dayParts(r.date);
              const pct = Math.min(100, (r.totals.productiveMs / (TARGET_HOURS * 3600000)) * 100);
              const met = pct >= 100;
              const open = r.status !== "clocked_out";

              return (
                <Box
                  key={r.id}
                  sx={{
                    display: { xs: "flex", md: "grid" },
                    flexDirection: "column",
                    gridTemplateColumns: "118px 1fr 120px 120px 150px",
                    gap: { xs: 1, md: 2 },
                    alignItems: { md: "center" },
                    px: 2,
                    py: 1.75,
                    borderBottom: `1px solid ${AGENT_LINE}`,
                    "&:hover": { bgcolor: "#F8FAFC" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: AGENT_INK }}>{day}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#94A3B8" }}>{weekday}</Typography>
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.75 }}>
                      <Typography sx={{ fontSize: 13, color: AGENT_INK, fontWeight: 600 }}>
                        {formatTime(r.clockInAt)} - {r.clockOutAt ? formatTime(r.clockOutAt) : "running"}
                      </Typography>
                      {r.lateBy > 0 && (
                        <Tooltip title={`${formatDuration(r.lateBy)} past rostered start`}>
                          <Chip
                            label="Late"
                            size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#FEF2F2", color: "#DC2626" }}
                          />
                        </Tooltip>
                      )}
                      {(r.clockInLocation || r.clockOutLocation) && (
                        <Tooltip
                          title={
                            <>
                              {r.clockInLocation && <div>In: {formatCoords(r.clockInLocation)}</div>}
                              {r.clockOutLocation && <div>Out: {formatCoords(r.clockOutLocation)}</div>}
                            </>
                          }
                        >
                          <Chip
                            component="a"
                            clickable
                            target="_blank"
                            rel="noreferrer"
                            href={mapsUrl(r.clockInLocation || r.clockOutLocation)}
                            label="Location"
                            size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#EEF2FF", color: "#0C237B" }}
                          />
                        </Tooltip>
                      )}
                      {r.autoClockedOut && (
                        <Tooltip title={r.clockOutNote || "Closed automatically"}>
                          <Chip
                            label="Auto out"
                            size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: "#FFFBEB", color: "#B45309" }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "#EEF2F7",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 3,
                          bgcolor: met ? "#16A34A" : "#FBBF24",
                        },
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: met ? "#16A34A" : AGENT_INK }}>
                      {formatDuration(r.totals.productiveMs)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94A3B8" }}>
                      of {TARGET_HOURS}h
                    </Typography>
                  </Box>

                  <Box>
                    <Tooltip
                      title={
                        r.breaks.length
                          ? r.breaks
                              .map((b) => `${breakLabel(b)}: ${formatDuration(b.durationMs)}`)
                              .join(" · ")
                          : "No breaks"
                      }
                    >
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#B45309" }}>
                          {formatDuration(r.totals.breakMs)}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#94A3B8" }}>
                          {r.breaks.length} break{r.breaks.length === 1 ? "" : "s"}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>

                  <Box>
                    <Chip
                      size="small"
                      label={open ? "In progress" : met ? "Full day" : "Short"}
                      sx={{
                        fontWeight: 700,
                        fontSize: 11.5,
                        bgcolor: open ? "#EEF2FF" : met ? "#ECFDF5" : "#FFF7ED",
                        color: open ? "#0C237B" : met ? "#16A34A" : "#C2410C",
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>

          {pages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
              <Pagination
                count={pages}
                page={page}
                onChange={(e, v) => setPage(v)}
                size="small"
                shape="rounded"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
